import {
  Box,
  Code,
  Divider,
  HStack,
  IconButton,
  Skeleton,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import {
  getGroupUrl,
  getLessonUrl,
  getStepUrl
} from "@pathwright/web/src/modules/utils/urls"
import { Fragment, MouseEvent, useMemo, useState } from "react"
import { useCopyToClipboard } from "react-use"
import ChakraTooltip from "../../components/ChakraTooltip"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { hasPathwrightStaffAccess } from "../../user/permissions"
import { getStepVerbIcon, getVerbForType } from "../utils"
import CardItem from "./CardItem"
import { useSyncCard } from "./SyncCardProvider"
import { SYNC_DIR_BACKWARD } from "./constants"
import { Change, ChangeFamily, ChangeSet } from "./types"
import {
  getChangeLabel,
  getPotentiallySyncableChangesCount,
  getSyncableChangesCount,
  getUnsyncableChangesCount
} from "./utils"

function isChangeFamily(change: Change | ChangeFamily): change is ChangeFamily {
  return !!(change as ChangeFamily).isFamily
}

type ChangeItemProps = {
  change: Change | ChangeFamily
  isPublishable?: boolean
}

const getIconForChange = (change: Change) => {
  let icon = "check-dash-square"

  if (change.assignment_type) {
    icon = getStepVerbIcon(getVerbForType(change.assignment_type))
  } else if (change.item_type === "lesson") {
    icon = "lesson"
  } else {
    icon = "step"
  }

  return icon as IconName
}

const getUnpublishableStatus = (change: Change, tc: any): string => {
  const isLesson = change.item_type === "lesson"

  if (isLesson) {
    switch (change.reason) {
      case "lesson_no_steps":
        return tc("Add Steps")
      case "lesson_no_valid_steps":
        // return tc("Add Valid Steps")
        return tc("")
      default:
        return tc("")
    }
  }

  switch (change.reason) {
    case "step_no_content":
      return tc("Add Blocks")
    case "step_unpublished_blocks":
      return tc("Save Blocks")
    case "step_draft_blocks":
      return tc("Save Blocks")
    default:
      return ""
  }
}

const ChangeItem = (props: ChangeItemProps) => {
  const { isPublishable } = props
  const {
    fromCohort,
    onClickStep,
    syncDir,
    selectedItemSourceIds,
    toggleSelectedItemSourceId,
    setSelectedItemSourceIds
  } = useSyncCard()
  const { t, tc } = useTranslate()

  const isCheckable = syncDir === SYNC_DIR_BACKWARD && isPublishable

  if (
    !setSelectedItemSourceIds ||
    !toggleSelectedItemSourceId ||
    !selectedItemSourceIds?.[syncDir]
  )
    return null

  const handleCheckItem = (change: Change) => (checked: boolean) => {
    if (toggleSelectedItemSourceId) {
      toggleSelectedItemSourceId(change.item_source_id)
    }
  }

  const getItemIsChecked = (change: Change) => {
    return (
      (isCheckable &&
        selectedItemSourceIds?.[syncDir]?.includes(change.item_source_id)) ||
      false
    )
  }

  const getCardItemProps = (change: Change, isDisabled?: boolean) => {
    const isStep = change.item_type === "step"
    const itemUrl = isStep
      ? getStepUrl(
          fromCohort?.resource?.slug || "",
          fromCohort?.id,
          change.item_source_id
        )
      : getLessonUrl(
          fromCohort?.resource?.slug || "",
          fromCohort?.id,
          change.item_source_id
        )

    return {
      onCheck: isCheckable ? handleCheckItem(change) : undefined,
      // Always include and disabled roerdered changes (they're required)
      isChecked: getItemIsChecked(change),
      isDisabled: isDisabled,
      ...(isPublishable
        ? {
            title: change.item_name,
            itemUrl: change.change_details?.deleted ? null : itemUrl,
            statusUrl: null,
            leftIcon: getIconForChange(change),
            status: getChangeLabel(change, t)
          }
        : {
            title: change.item_name,
            itemUrl,
            statusUrl: itemUrl,
            onClick: (event: MouseEvent) => {
              if (onClickStep) {
                event.stopPropagation()
                event.preventDefault()
                onClickStep(change.item_source_id)
              }
            },
            status: getUnpublishableStatus(change, tc),
            leftIcon: getIconForChange(change)
          })
    }
  }

  const renderChange = () => {
    const isFamily = isChangeFamily(props.change)

    if (isFamily) {
      const { parent, children } = props.change as ChangeFamily

      // Prevent orphaned Steps by
      // 1. disabling child Steps if their parent Lesson change is deleted or created
      // 2. checking or unchecking Steps when their parent Lesson is checked or unchecked
      const parentIsDeleted = parent.change_details?.deleted
      const parentIsCreated = parent.change_details?.created
      const childrenAreDisabled = parentIsDeleted || parentIsCreated

      const handleCheckParent = (checked: boolean) => {
        setSelectedItemSourceIds((prev) => ({
          ...prev,
          [syncDir]: checked
            ? [
                ...new Set([
                  // Remove duplicates
                  ...(prev[syncDir] || []), // Preserve existing IDs
                  parent.item_source_id, // Add parent
                  ...children.map((child) => child.item_source_id) // Add all children
                ])
              ]
            : (prev[syncDir] || []).filter(
                // Preserve existing IDs
                (id) =>
                  id !== parent.item_source_id && // Remove parent
                  !children.some((child) => child.item_source_id === id) // Remove all children
              )
        }))
      }

      return (
        <VStack w="full" align="start" gap={0}>
          <CardItem
            {...getCardItemProps(parent)}
            onCheck={isCheckable ? handleCheckParent : undefined}
          />
          <VStack w="full" align="start" gap={0}>
            {children.map((child: Change, index: number) => (
              <CardItem
                key={`change-${index}`}
                {...getCardItemProps(child, childrenAreDisabled)}
                isChild
              />
            ))}
          </VStack>
        </VStack>
      )
    }

    return <CardItem {...getCardItemProps(props.change as Change)} />
  }

  return (
    <HStack
      w="full"
      sx={{
        ".PathPublishCard__item": {
          boxShadow: "none",
          paddingTop: "var(--chakra-space-1)",
          paddingBottom: "var(--chakra-space-1)",
          paddingRight: "var(--chakra-space-0)",
          paddingLeft: "var(--chakra-space-0)",
          fontSize: "md",
          color: "meta",
          border: "none",
          overflow: "hidden"
        },
        ".PathPublishCard__item > .chakra-link": {
          overflow: "hidden"
        },
        svg: {
          width: "1em",
          height: "1em"
        },
        ".PathPublishCard__item-status": {
          fontStyle: "normal",
          whiteSpace: "normal",
          flexShrink: 0
        }
      }}
      _hover={{
        ".PathPublishCard__item": {
          bg: "transparent"
        }
      }}
    >
      {renderChange()}
    </HStack>
  )
}

const getListsFromChanges = (changes: ChangeSet) => {
  const {
    syncable: baseSyncable,
    stepsNoContent,
    stepsDraftBlocks,
    stepsUnpublishedBlocks,
    lessonsNoSteps,
    lessonsNoValidSteps
  } = changes

  const baseUnSyncable = [
    ...stepsNoContent,
    ...stepsDraftBlocks,
    ...stepsUnpublishedBlocks,
    ...lessonsNoSteps,
    ...lessonsNoValidSteps
  ]

  // Render changes as a hiearchy, if changes exist for path parent and child(ren)
  // Example: a change for a Lesson and at least one of its Steps
  // Otherwise, render items as standalone changes
  // Sort standalone changes by parent order first, then by item order
  const handleFamilies = (changes: Change[]) => {
    return changes
      .map((change: Change) => {
        const children =
          change.item_type === "lesson" || change.item_type === "resource"
            ? changes
                .filter((c) => c.parent_source_id === change.item_source_id)
                .sort((a, b) => (a?.item_order ?? 0) - (b?.item_order ?? 0)) // Sort children by item_order
            : []

        if (children.length) {
          return {
            isFamily: true,
            parent: change,
            children,
            item_order: change.item_order ?? 0, // Use parent's item_order for sorting the family
          } as ChangeFamily
        }

        const hasParent =
          change.item_type === "step" &&
          changes.some((c) => c.item_source_id === change.parent_source_id)

        if (hasParent) {
          return null // Exclude orphaned child steps; already included in their parent's family
        }

        return change // Return standalone change
      })
      .filter((item): item is Change | ChangeFamily => item !== null) // Remove nulls and enforce type safety
      .sort((a, b) => {
        // Determine sorting keys
        const aParentOrder =
          "parent" in a
            ? a.parent.item_order ?? 0
            : a.parent_item_order ?? 0
        const bParentOrder =
          "parent" in b
            ? b.parent.item_order ?? 0
            : b.parent_item_order ?? 0

        // Compare by parent_item_order first
        const parentOrderComparison = aParentOrder - bParentOrder
        if (parentOrderComparison !== 0) {
          return parentOrderComparison
        }

        // Compare by item_order
        const aOrder =
          "parent" in a ? a.parent.item_order ?? 0 : a.item_order ?? 0
        const bOrder =
          "parent" in b ? b.parent.item_order ?? 0 : b.item_order ?? 0

        return aOrder - bOrder
      }) as (Change | ChangeFamily)[]
  }

  const syncable = handleFamilies(baseSyncable)
  const unSyncable = handleFamilies(baseUnSyncable)

  return { syncable, unSyncable }
}

// Superuser can view/copy sync plan details.
function useSuperUserSyncPlanDetails() {
  const { syncPlan } = useSyncCard()
  const pwContext = usePathwrightContext()
  const canViewSyncPlanDetails = hasPathwrightStaffAccess(pwContext)
  const [showingSyncPlanDetails, setShowingSyncPlanDetails] = useState(false)
  const [_, onCopy] = useCopyToClipboard()
  const toast = useToast()
  const handleCopy = () => {
    onCopy(JSON.stringify(syncPlan?.query.data, null, 2))
    toast({
      title: "Copied!",
      status: "success",
      duration: 1000,
      isClosable: true,
      icon: <Pathicon icon="check" />
    })
  }

  function handleToggle() {
    setShowingSyncPlanDetails((val) => !val)
  }

  return canViewSyncPlanDetails
    ? {
        handleCopy: handleCopy,
        handleToggle: handleToggle,
        showingSyncPlanDetails
      }
    : null
}

const ChangeList = () => {
  const { changes, syncPlan, loading, fromCohort } = useSyncCard()

  const { syncable, unSyncable } = useMemo(
    () => getListsFromChanges(changes as ChangeSet),
    [changes]
  )

  const { t, tc } = useTranslate()

  const syncableCount = getSyncableChangesCount(syncPlan)
  const potentiallySyncableCount = getPotentiallySyncableChangesCount(syncPlan)
  const unsyncableCount = getUnsyncableChangesCount(syncPlan)

  // We only show a single reorder change, if any exists
  const hasReorder = syncPlan?.willReorder

  // Superuser can view/copy sync plan details.
  const superUserSyncPlanDetails = useSuperUserSyncPlanDetails()

  return (
    <VStack
      gap={"syncMd"}
      bg="white"
      w="full"
      align="start"
      sx={{
        ".PathPublishCard__item": {
          bg: "transparent",
          boxShadow: "none",
          color: "rgba(68, 68, 68, 0.9)"
        },
        ".PathPublishCard__item-status": {
          color: "rgba(68, 68, 68, 0.6)"
        },
        ".PathPublishCard__item-title": {
          color: "rgba(68, 68, 68, 0.9)"
        },
        ".PathPublishCard__item-left-icon": {
          color: "rgba(68, 68, 68, 0.9)"
        }
      }}
    >
      {!unSyncable.length && !potentiallySyncableCount ? (
        <Skeleton
          isLoaded={!loading}
          fitContent
          w="full"
          minH="120px"
          borderRadius="card"
        >
          <Box
            borderRadius="card"
            bgColor="backgroundGrayLight"
            color="textOnLight"
            w="full"
            p="syncMd"
          >
            <CardItem
              title={tc("Add at least one lesson and step")}
              itemUrl={getGroupUrl(
                fromCohort?.resource?.slug || "",
                fromCohort?.id
              )} // get path url
              icon="chevron-right"
            />
          </Box>
        </Skeleton>
      ) : (
        <>
          {potentiallySyncableCount ? (
            <VStack
              w="full"
              align="start"
              gap={1}
              backgroundColor="rgba(26, 32, 44, 0.1)"
              color="#444"
              borderRadius="card"
              p="syncMd"
            >
              {superUserSyncPlanDetails?.showingSyncPlanDetails ? (
                // Super user can view sync plan details.
                <VStack maxW="100%">
                  <HStack ml="auto">
                    <IconButton
                      aria-label="copy sync plan details"
                      icon={<Pathicon icon="copy" />}
                      variant="ghost"
                      onClick={superUserSyncPlanDetails.handleCopy}
                      h={8}
                      minW={8}
                      isRound
                    />
                    <IconButton
                      aria-label="switch to change list"
                      icon={<Pathicon icon="sparkles" />}
                      variant="solid"
                      onClick={superUserSyncPlanDetails.handleToggle}
                      h={8}
                      minW={8}
                      isRound
                    />
                  </HStack>
                  <Box maxW="100%" overflow="auto">
                    <Code
                      bg="transparent"
                      whiteSpace="pre"
                      children={JSON.stringify(syncPlan?.query.data, null, 2)}
                    />
                  </Box>
                </VStack>
              ) : (
                <>
                  <HStack align="center" gap={0} w="100%">
                    <Text my={0} color="#444">
                      <strong>{`${syncableCount} ${t("draft {{ change }}", {
                        change: t("change", { count: syncableCount })
                      })}`}</strong>
                    </Text>
                    {!!superUserSyncPlanDetails && (
                      // Super user can view sync plan details.
                      <HStack ml="auto">
                        <ChakraTooltip
                          title="Only visible to superusers"
                          fitContent
                        >
                          <IconButton
                            aria-label="switch to sync plan details"
                            icon={<Pathicon icon="sparkles" />}
                            variant="ghost"
                            onClick={superUserSyncPlanDetails.handleToggle}
                            h={8}
                            minW={8}
                            isRound
                          />
                        </ChakraTooltip>
                      </HStack>
                    )}
                  </HStack>
                  <VStack gap={0} my={0} w="full">
                    {syncable
                      // .filter(
                      //   (change) =>
                      //     // Ignore ChangeFamily.
                      //     "isFamily" in change ||
                      //     // Ignore reorder changes that are included in the syncable list.
                      //     !hasChange(changes.ignored, change)
                      // )
                      .map((change: Change | ChangeFamily, index) => (
                        <Fragment key={`syncable-change-${index}`}>
                          <ChangeItem change={change} isPublishable />
                          {index !== syncable.length - 1 && <Divider my={0} />}
                        </Fragment>
                      ))}
                    {hasReorder ? (
                      <HStack
                        sx={{
                          ".PathPublishCard__item": {
                            paddingTop: "var(--chakra-space-1)",
                            paddingBottom: "var(--chakra-space-1)",
                            paddingRight: "var(--chakra-space-0)",
                            paddingLeft: "var(--chakra-space-0)"
                          },
                          ".PathPublishCard__item-status": {
                            fontStyle: "normal"
                          },
                          svg: {
                            width: "1em",
                            height: "1em"
                          }
                        }}
                        w="full"
                      >
                        <CardItem
                          onCheck={undefined}
                          // Always include roerdered changes (they're required and can't be unchecked)
                          isChecked={true}
                          isDisabled={true}
                          title={tc("Reordered")}
                          itemUrl={null}
                          leftIcon={"flip-vertical" as IconName}
                          status={tc("Path Order Updated")}
                        />
                      </HStack>
                    ) : null}
                  </VStack>
                </>
              )}
            </VStack>
          ) : null}
          {unsyncableCount ? (
            <VStack
              w="full"
              align="start"
              gap={1}
              backgroundColor="rgba(26, 32, 44, 0.1)"
              color="#444"
              borderRadius="card"
              p="syncMd"
              sx={{
                ".PathPublishCard__item-status": {
                  textDecoration: "underline"
                }
              }}
              _hover={{
                ".PathPublishCard__item-status": {
                  textDecoration: "underline"
                }
              }}
            >
              <HStack align="center" gap={0}>
                <Text my={0} color="#444">
                  <strong>
                    {unsyncableCount}{" "}
                    {t(`{{ change }} not ready`, {
                      change: t("change", { count: unsyncableCount })
                    })}
                  </strong>
                </Text>
              </HStack>
              <VStack gap={0} my={0} w="full">
                {unSyncable.map(
                  (change: Change | ChangeFamily, index: number) => (
                    <Fragment key={`unpublishable-change-${index}`}>
                      <ChangeItem change={change} />
                      {index !== unSyncable.length - 1 && <Divider my={0} />}
                    </Fragment>
                  )
                )}
              </VStack>
            </VStack>
          ) : null}
        </>
      )}
    </VStack>
  )
}

export default ChangeList
